import { CampaignRecommendationResult } from '@/client/campaigns';
import AuthorityTrustIcon from '@/images/phishing/signs/authority-trust.svg';
import ContextIcon from '@/images/phishing/signs/context.svg';
import CuriosityIcon from '@/images/phishing/signs/curiosity.svg';
import DoTheRightThingIcon from '@/images/phishing/signs/do-the-right-thing.svg';
import FearIcon from '@/images/phishing/signs/fear.svg';
import MediaIcon from '@/images/phishing/signs/media.svg';
import RewardsIcon from '@/images/phishing/signs/rewards.svg';
import TimeSensitivityIcon from '@/images/phishing/signs/time-sensitivity.svg';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Timeline } from 'primereact/timeline';
import { ToggleButton } from 'primereact/togglebutton';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const RecommendationsContainer = styled.div`
  margin-top: 2rem;

  .p-timeline {
    .p-timeline-event-opposite {
      display: none;
    }

    .p-timeline-event-content {
      margin-bottom: 1rem;
    }

    .p-timeline-event-marker {
      width: 2rem;
      height: 2rem;
      border: 1px solid var(--gray-border-lighter);
      border-radius: var(--medium-border-radius);
      align-items: center;
      justify-content: center;
    }
  }

  .p-togglebutton.p-button.p-highlight,
  .p-togglebutton.p-button.p-highlight .p-button-icon-left,
  .p-togglebutton.p-button.p-highlight .p-button-icon-right {
    color: #4b5563;
  }
`;

const RecommendationCard = styled.div`
  background-color: white;
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--medium-border-radius);
  padding: 1.5rem;
`;

const CampaignSignTag = styled(Tag)`
  border-radius: var(--medium-border-radius);
  background: rgba(14, 151, 151, 0.1);
  color: var(--green-color);
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;

  .p-tag-value {
    font-size: var(--xxsmall-font-size);
    line-height: var(--xxsmall-line-height);
  }
`;

const DetailedExplanation = styled.div`
  padding: 2rem;
  background: var(--beige-main);
  border-radius: var(--medium-border-radius);

  h3 {
    margin: 0;
  }
`;

const StyledToggleButton = styled(ToggleButton)`
  border: none;
  background: white;
  font-size: var(--small-font-size);
  color: rgba(0, 0, 0, 0.8);

  .p-button-label {
    font-weight: 400;
  }

  &:hover {
    border: none;
  }

  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const TimingInfoCard = styled(Card)`
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;

  .p-card-body {
    padding: 1.5rem;
  }

  .p-card-title {
    color: #1e293b;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .p-card-content {
    color: #475569;
  }
`;

// Mapping of phishing signs to their corresponding SVG icons
const phishingSignIcons: Record<string, string> = {
  'authority-trust': AuthorityTrustIcon,
  context: ContextIcon,
  curiosity: CuriosityIcon,
  'do-the-right-thing': DoTheRightThingIcon,
  fear: FearIcon,
  media: MediaIcon,
  rewards: RewardsIcon,
  'time-sensitivity': TimeSensitivityIcon,
};

type CampaignRecommendationsProps = {
  data: CampaignRecommendationResult;
};

// Timing Analysis Component
const TimingAnalysisSection: React.FC<{ timingAnalysis: any }> = ({
  timingAnalysis,
}) => {
  const { t } = useTranslation();

  return (
    <TimingInfoCard
      title={t('campaign.recommendation.timing-analysis', 'Timing Analysis')}
    >
      <FlexContainer direction="column" align="start" gap={1}>
        <div>
          <strong>
            {t(
              'campaign.recommendation.next-launch-date',
              'Next Available Launch Date',
            )}
            :
          </strong>{' '}
          {<FormatDate date={timingAnalysis.nextAvailableLaunchDate} />}
        </div>
        <div>
          <strong>
            {t(
              'campaign.recommendation.total-plan-duration',
              'Total Plan Duration',
            )}
            :
          </strong>{' '}
          {timingAnalysis.totalPlanDuration}{' '}
          {t('campaign.recommendation.days', 'days')}
        </div>
        <div>
          <strong>
            {t(
              'campaign.recommendation.spacing-rationale',
              'Campaign Spacing Rationale',
            )}
            :
          </strong>{' '}
          {timingAnalysis.spacingRationale}
        </div>
        {timingAnalysis.seasonalConsiderations?.length > 0 && (
          <div>
            <strong>
              {t(
                'campaign.recommendation.seasonal-considerations',
                'Seasonal Considerations',
              )}
              :
            </strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              {timingAnalysis.seasonalConsiderations.map(
                (consideration: string, index: number) => (
                  <li key={index}>{consideration}</li>
                ),
              )}
            </ul>
          </div>
        )}
      </FlexContainer>
    </TimingInfoCard>
  );
};

export const CampaignRecommendations: React.FC<
  CampaignRecommendationsProps
> = ({ data }) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<number[]>([]);

  const toggleSection = useCallback((index: number) => {
    setExpandedSections((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  }, []);

  // Memoized custom content to prevent unnecessary re-renders
  const customContent = useCallback(
    (_item: any, index: number) => {
      const campaign = data.plannedCampaigns[index];

      if (!campaign) {
        return null;
      }

      const isExpanded = expandedSections.includes(index);
      // Get the appropriate icon URL for this sign
      const iconUrl = phishingSignIcons[campaign.sign];

      return (
        <RecommendationCard>
          <FlexContainer justify="space-between" align="center">
            <div>
              <h3 className="mb-0 mt-0">{campaign.name}</h3>
              <CampaignSignTag
                severity="info"
                value={t(`campaign.template.${campaign.sign}`)}
                rounded={true}
              />
            </div>
            {iconUrl && (
              <div aria-hidden="true">
                <img src={iconUrl} alt={campaign.sign} width={38} height={38} />
              </div>
            )}
          </FlexContainer>
          <p className="text-sm">{campaign.explanation}</p>
          {/* Enhanced information for planned campaigns */}
          <div
            style={{
              marginBottom: '1rem',
              fontSize: '12px',
              color: '#64748b',
            }}
          >
            {campaign.launchDate && (
              <div style={{ marginBottom: '0.25rem' }}>
                <strong>Suggested Launch Date:</strong>{' '}
                <FormatDate date={campaign.launchDate} />
              </div>
            )}
            {campaign.seasonalContext && (
              <div style={{ marginBottom: '0.25rem' }}>
                <strong>Seasonal Context:</strong> {campaign.seasonalContext}
              </div>
            )}
          </div>
          <StyledToggleButton
            onIcon="pi pi-arrow-up"
            offIcon="pi pi-arrow-down"
            checked={isExpanded}
            onChange={(e) => {
              e.preventDefault();
              toggleSection(index);
            }}
            onLabel={t('campaign.recommendation.hide-details')}
            offLabel={t('campaign.recommendation.show-details')}
            aria-expanded={isExpanded}
            aria-controls={`recommendation-details-${index}`}
          />

          {isExpanded && (
            <DetailedExplanation
              id={`recommendation-details-${index}`}
              role="region"
              aria-label={t('campaign.recommendation.detailed-analysis')}
            >
              <h3>{t('campaign.recommendation.analysis-title')}</h3>
              <p className="text-sm">{data.analysis}</p>

              {data.campaignPerformanceAnalysis && (
                <>
                  <h3>
                    {t(
                      'campaign.recommendation.campaign-performance-analysis-title',
                    )}
                  </h3>
                  <p className="text-sm">{data.campaignPerformanceAnalysis}</p>
                </>
              )}

              {data.signsPerformanceAnalysis && (
                <>
                  <h3>
                    {t(
                      'campaign.recommendation.sings-performance-analysis-title',
                    )}
                  </h3>
                  <p className="text-sm">{data.signsPerformanceAnalysis}</p>
                </>
              )}

              <h3>
                {t('campaign.recommendation.progression-rationale-title')}
              </h3>
              <p className="text-sm">{campaign.progressionRationale}</p>

              <h3>{t('campaign.recommendation.number-of-campaigns-title')}</h3>
              <p className="text-sm">{data.numberOfTestedCampaigns}</p>

              <h3>
                {t(
                  'campaign.recommendation.number-of-signs-already-tested-title',
                )}
              </h3>
              <p className="text-sm">{data.numberOfTestedSigns}</p>
            </DetailedExplanation>
          )}
        </RecommendationCard>
      );
    },
    [data, expandedSections, t, toggleSection],
  );

  // Memoized custom marker
  const customMarker = useCallback(
    (item: any, index: number) => {
      return (
        <div
          className="p-timeline-event-marker"
          aria-label={t('campaign.recommendation.step', { step: index + 1 })}
        >
          {index + 1}
        </div>
      );
    },
    [t],
  );

  // Early return with proper error handling
  if (!data.plannedCampaigns.length) {
    return (
      <div role="alert" aria-live="polite">
        {t('campaign.recommendation.no-recommendations')}
      </div>
    );
  }

  return (
    <RecommendationsContainer>
      {/* Show timing analysis if available */}
      {data.timingAnalysis && (
        <TimingAnalysisSection timingAnalysis={data.timingAnalysis} />
      )}

      <Timeline
        value={data.plannedCampaigns}
        content={customContent}
        marker={customMarker}
      />
    </RecommendationsContainer>
  );
};
